import type { FormaPagamentoLoja } from "@/api"
import type { MetodoTef } from "@/lib/tef/elgin"
import { metodosDaForma } from "@/lib/tef/metodos-da-forma"
import { METODOS_PAGAMENTO, type MetodoPagamento } from "./payment-methods"

/** Passos do pagamento no totem de checkout, no molde do "Finalizar" do totem de pedidos. */
export type PassoPagamento = 'VALOR' | 'FORMA' | 'PAGAMENTO' | 'CPF_NOTA' | 'NOTA'

export const ROTULO_PASSO_PAGAMENTO: Record<PassoPagamento, string> = {
    VALOR: 'Valor',
    FORMA: 'Forma de pagamento',
    PAGAMENTO: 'Pagamento',
    CPF_NOTA: 'CPF na nota',
    NOTA: 'Nota fiscal',
}

/** Etapas do pós-pagamento (`pos-pagamento/index.tsx`). */
export type EtapaPosPagamento = 'cpf-pergunta' | 'cpf-input' | 'impressao-pergunta' | 'emitindo' | 'final'

export interface SituacaoPagamento {
    valorDefinido: boolean
    /** Forma tocada ou cobrança em curso. */
    pagando: boolean
    /** Etapa do pós-pagamento; `null` antes dele. */
    etapaPos: EtapaPosPagamento | null
}

export interface SituacaoDosPassos {
    dividindo: boolean
    /** O checkout emite NFC-e. */
    emiteNota: boolean
    /** Este pagamento quita a conta: a nota só é emitida para quem fecha. */
    fechaAConta: boolean
    /** O totem pergunta o CPF da nota (o lojista pode desligar). */
    perguntaCpf: boolean
    /** Forma de pagar no caixa: quem fecha a conta e emite a nota é o caixa. */
    pagaNoCaixa?: boolean
}

/** O valor só existe ao dividir a conta; CPF e nota, só para quem fecha a conta na maquininha de um checkout que emite NFC-e. */
export function passosDoPagamento({ dividindo, emiteNota, fechaAConta, perguntaCpf, pagaNoCaixa = false }: SituacaoDosPassos): PassoPagamento[] {
    const temNota = emiteNota && fechaAConta && !pagaNoCaixa

    return [
        ...(dividindo ? (['VALOR'] as const) : []),
        'FORMA',
        'PAGAMENTO',
        ...(temNota && perguntaCpf ? (['CPF_NOTA'] as const) : []),
        ...(temNota ? (['NOTA'] as const) : []),
    ]
}

/** Índice do passo ativo no stepper; o tamanho da lista quando tudo terminou (todos concluídos). */
export function passoAtivo(passos: PassoPagamento[], { valorDefinido, pagando, etapaPos }: SituacaoPagamento): number {
    const indice = (passo: PassoPagamento): number => passos.indexOf(passo)

    if (etapaPos === 'final') return passos.length
    if (etapaPos === 'cpf-pergunta' || etapaPos === 'cpf-input') return indice('CPF_NOTA')
    if (etapaPos != null) return passos.includes('NOTA') ? indice('NOTA') : indice('PAGAMENTO')
    if (pagando) return indice('PAGAMENTO')
    if (!valorDefinido && passos.includes('VALOR')) return indice('VALOR')

    return indice('FORMA')
}

/** Na maquininha do totem (TEF) ou no caixa (LOCAL); a ONLINE é gateway do delivery e não roda no totem. `undefined` enquanto carrega. */
export function formasDoCheckout(formas: FormaPagamentoLoja[] | undefined): FormaPagamentoLoja[] | undefined {
    return formas?.filter((fp) => fp.tipo === 'TEF' || fp.tipo === 'LOCAL')
}

/** O totem não recebe dinheiro: forma que não é da maquininha se paga no caixa. */
export function pagaNoCaixa(forma: FormaPagamentoLoja): boolean {
    return forma.tipo !== 'TEF'
}

/** Métodos da forma, sempre na ordem crédito, débito, PIX. */
export function metodosDoCheckout(forma: FormaPagamentoLoja): MetodoPagamento[] {
    const aceitos = metodosDaForma(forma.forma, forma.metodos)

    return METODOS_PAGAMENTO.filter((m) => aceitos.includes(m.metodoTef))
}

/** Linha de apoio do cartão de qualquer forma: na maquininha, o que se faz nela; paga no balcão, onde pagar. */
export function descreverForma(forma: FormaPagamentoLoja): string {
    if (forma.tipo !== 'TEF') return 'Pague no caixa'

    return descreverMetodos(metodosDoCheckout(forma).map((m) => m.metodoTef))
}

/** Linha de apoio do cartão da forma: o que o cliente vai fazer na maquininha. */
export function descreverMetodos(metodos: MetodoTef[]): string {
    const pix = metodos.includes('PIX')
    const credito = metodos.includes('CREDIT_CARD')
    const debito = metodos.includes('DEBIT_CARD')

    if (pix && (credito || debito)) return 'Cartão ou PIX na maquininha'
    if (pix) return 'PIX pelo QR code na maquininha'
    if (credito && debito) return 'Crédito ou débito na maquininha'

    return debito ? 'Débito na maquininha' : 'Crédito na maquininha'
}
